/** Angular imports */
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { Component, computed, DestroyRef, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { catchError, map, of, switchMap } from 'rxjs';

/** Core imports */
import { ExportService, HostsService, LicensesService, UsersService } from '@core';

/** Component imports */
import {
    Button,
    Card,
    Checkbox,
    Modal,
    Popover,
    Tabs,
    ToggleButton,
} from '@ntv360/component-pantry';
import { StatsCardComponent, TablePanelComponent, TableToolbarButton } from '@shared';
import { EditScreenComponent } from '../edit-screen/edit-screen.component';
import { ActivityComponent, ContentsComponent } from './tabs';

/** Local imports */
import { ApiHostLocation } from '@core/services/hosts/hosts.interfaces';
import { UILicense } from '@features';
import { LICENSE_COLUMNS_TAB } from '@features/hosts/constants';
import { UIHostData } from '@features/hosts/interface';
import { SVG_ICONS } from '@shared/constants';
import { Logger, withLoading } from '@shared/utils';
import { BUSINESS_HOURS, QUICK_ACTION_OPTIONS, TAGS } from '../../constants';

@Component({
    selector: 'app-single-host',
    imports: [
        Card,
        StatsCardComponent,
        Tabs,
        Button,
        ActivityComponent,
        ContentsComponent,
        TablePanelComponent,
        Modal,
        EditScreenComponent,
        Checkbox,
        Popover,
        ToggleButton,
    ],
    templateUrl: './single-host.component.html',
    styleUrl: './single-host.component.scss',
})
export class SingleHostComponent {
    /**
     * Injected hosts service for fetching host location details.
     */
    private hostsService = inject(HostsService);

    /**
     * Injected export service for CSV export.
     */
    private exportService = inject(ExportService);

    /**
     * Injected licenses service for license-related API operations.
     */
    private licensesService = inject(LicensesService);

    /**
     * Injected users service for user-related API operations.
     */
    private usersService = inject(UsersService);

    /**
     * Injected activated route to access URL parameters.
     */
    private route = inject(ActivatedRoute);

    /**
     * Injected DestroyRef for automated cleanup of RxJS subscriptions.
     */
    private destroyRef = inject(DestroyRef);

    /**
     * Column definitions for the license table.
     */
    public licenseColumns = LICENSE_COLUMNS_TAB;

    /**
     * View Child for the quick action popover
     */
    protected quickActionPopover = viewChild<Popover>('quickActionPopover');

    /**
     * The host data to be displayed.
     */
    public hostData = signal<UIHostData | null>(null);

    /**
     * Raw API host data passed to the edit modal for pre-filling the form.
     */
    public rawHostData = signal<ApiHostLocation | null>(null);

    /**
     * The business hours to be displayed.
     */
    public businessHours = BUSINESS_HOURS;

    /**
     * Reference to navbar icon constants.
     */
    public icons = SVG_ICONS;

    /**
     * Path to the SVG sprite file for navbar icons.
     */
    public spritePath = 'assets/icons/icons-sprite.svg';

    /**
     * The tags to be displayed.
     */
    public tags = TAGS;

    /**
     * Loading state for the component.
     */
    public isLoading = signal<boolean>(true);

    /**
     * Tabs configuration for the single license page
     */
    public tabs = [{ label: 'Licenses' }, { label: 'Contents' }, { label: 'Activity' }];

    /**
     * Active tab index
     */
    public activeTab = signal(0);

    /**
     * Whether to show the edit screen modal
     */
    public isEditModalVisible = signal(false);

    /**
     * Active state of the host, bound to the toggle in the edit modal header.
     */
    public isHostActive = signal(false);

    /**
     * Whether to show the assign license modal
     */
    public isAssignModalVisible = signal(false);

    /**
     * Options for the quick action dropdown.
     */
    public quickActionOptions = QUICK_ACTION_OPTIONS;

    /**
     * Signal to track selected licenses in the assign modal.
     */
    public selectedAssignLicenseIds = signal<Set<string>>(new Set());

    /**
     * Data for the license table as a signal so templates can call `licenseData()`.
     */
    public licenseData = signal<UILicense[]>([]);

    /**
     * Loading state specifically for the licenses table.
     */
    public isLicensesLoading = signal<boolean>(true);

    /**
     * Signal to track selected rows in the table.
     */
    public selectedRows = signal<UILicense[]>([]);
    /**
     * Signal for search term.
     */
    public searchTerm = signal<string>('');

    /**
     * Signal to trigger data refresh.
     */
    public refreshTrigger = signal<number>(0);

    /**
     * Observables for search and refresh, defined at class level for injection context.
     */
    private searchTerm$ = toObservable(this.searchTerm);
    private refresh$ = toObservable(this.refreshTrigger);

    /**
     * Signal for showing the bulk delete confirmation modal.
     */
    public showDeleteModal = signal(false);

    /**
     * Signal for showing the bulk deletion success notification modal.
     */
    public showSuccessModal = signal(false);

    /**
     * Standard Angular lifecycle hook for component initialization.
     * Fetches the host ID from the route and initializes data streams.
     *
     * @returns {void}
     */
    ngOnInit(): void {
        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
            this.initHostData(id);
            this.initLicensesStream(id);
        }
    }

    /**
     * Initializes host location data for a specific ID.
     *
     * @param {string} id - The host location ID from the URL parameters.
     * @returns {void}
     */
    private initHostData(id: string): void {
        this.hostsService
            .getHostById(id)
            .pipe(
                withLoading(this.isLoading),
                switchMap((host) =>
                    this.usersService.getUserById(host.createdBy).pipe(
                        map((userRes) => ({
                            host,
                            createdByName: `${userRes.data.firstName} ${userRes.data.lastName}`,
                        })),
                        catchError(() => of({ host, createdByName: host.createdBy || 'N/A' })),
                    ),
                ),
                catchError((error) => {
                    Logger.error('SingleHostComponent', 'Error fetching host data:', error);
                    return of(null);
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((result) => {
                if (result) {
                    console.log('Single host details:', result.host);
                    const mapped = this.mappedHostData(result.host, result.createdByName);
                    this.hostData.set(mapped);
                    this.rawHostData.set(result.host);
                    this.isHostActive.set(mapped.status === 'Active');
                    console.log(this.hostData(), 'this is the host data');
                }
            });
    }

    /**
     * Initializes the reactive license stream specifically for this host location.
     *
     * Similar to the global licenses view, this orchestrates a data stream that reacts to:
     * - `searchTerm$`: Debounced search queries.
     * - `refreshTrigger$`: Manual refresh events.
     * - `filters$`: An immutable filter restricted to the current host location ID.
     *
     * It updates the `licenseData` and `isLicensesLoading` signals upon each successful emission.
     * Automatically cleans up via `takeUntilDestroyed`.
     *
     * @param {string} id - The unique host location ID used as a primary filter.
     * @returns {void}
     * @private
     */
    private initLicensesStream(id: string): void {
        const filters$ = of({ hostLocationId: { _eq: id } });

        this.licensesService
            .getLicensesStream({
                searchTerm$: this.searchTerm$,
                refreshTrigger$: this.refresh$,
                filters$,
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response) => {
                this.licenseData.set(response.data);
                this.isLicensesLoading.set(false);
            });
    }

    /**
     * Maps the raw API host location data into a format suitable for the UI.
     *
     * @param {ApiHostLocation} host - The raw host location object from the API.
     * @returns {UIHostData} The mapped UI data structure.
     */
    private mappedHostData(host: ApiHostLocation, createdByName: string): UIHostData {
        return {
            id: { label: 'ID', value: host.hostLocationId },
            name: { label: 'Name', value: host.hostLocationName },
            image: host.logo || 'n-compass logo.png',
            status: host.statusId === 1 ? 'Active' : 'Inactive',
            dealerInfo: [
                { label: 'Category', value: host.category || 'N/A' },
                {
                    label: 'Address',
                    value: `${host.addressLine1}, ${host.city}, ${host.state} ${host.zip}`,
                },
                { label: 'Assigned Dealer', value: 'N/A' },
                { label: 'Created by', value: createdByName },
                { label: 'Notes', value: host.notes || 'N/A' },
            ],
            address: {
                label: 'Address',
                value: host.addressLine1,
            },
            businessHours: {
                label: 'Business Hours',
                value: host.businessHours ? JSON.stringify(host.businessHours) : 'N/A',
            },
            category: { label: 'Category', value: host.category || 'N/A' },
            notes: { label: 'Notes', value: host.notes || 'N/A' },
            tags: { label: 'Tags', value: host.tags ?? [] },
        };
    }

    /**
     * Handle quick action selection
     * @param option The selected dropdown option
     */
    public onQuickAction(value: string): void {
        console.log('Quick action selected:', value);
        // Implement logic for each action if needed
    }

    /**
     * List of stats cards to display.
     */
    /**
     * Computed signal generating the list of stats cards data.
     * Calculates totals, active counts, and percentages based on current license data.
     *
     * @returns {Array<Object>} An array of formatted statistics card objects.
     */
    public statsData = computed(() => {
        const licenses = this.licenseData();
        const total = licenses.length;
        const active = licenses.filter(
            (l) => l.displayStatus === 'ON' || l.displayStatus === 'Active',
        ).length;
        const inactive = total - active;

        return [
            {
                title: 'Total Licenses',
                description: 'Number of licenses at this host',
                iconKey: 'licenses',
                customColor: { iconColor: '#2db5a4', bgColor: '#e3fae1' },
                total: total.toString(),
                stats: [
                    { label: 'Active', value: active.toString(), colorClass: 'text-green-600' },
                    { label: 'Inactive', value: inactive.toString(), colorClass: 'text-red-main' },
                ],
            },
            {
                title: 'Active Licenses',
                description: 'Licenses currently online',
                iconKey: 'licenses',
                customColor: { iconColor: '#709ff8', bgColor: '#eef4fe' },
                total: active.toString(),
                stats: [
                    {
                        label: 'Percentage',
                        value: total > 0 ? `${((active / total) * 100).toFixed(1)}%` : '0%',
                        colorClass: 'text-blue-500',
                    },
                ],
            },
            {
                title: 'Inactive Licenses',
                description: 'Licenses currently offline',
                iconKey: 'licenses',
                customColor: { iconColor: '#e73535', bgColor: '#fdf1f1' },
                total: inactive.toString(),
                stats: [
                    {
                        label: 'Percentage',
                        value: total > 0 ? `${((inactive / total) * 100).toFixed(1)}%` : '0%',
                        colorClass: 'text-red-main',
                    },
                ],
            },
        ];
    });

    /**
     * Computed list of licenses that don't have an assigned host.
     */
    public unassignedLicenses = computed(() => {
        return this.licenseData().filter((license) => !license.host);
    });

    /**
     * Handles tab change events
     * @param index - The index of the selected tab
     */
    public onTabChange(index: number): void {
        this.activeTab.set(index);
    }

    /**
     * Scroll Dispatcher for scroll event
     */
    private scrollDispatcher = inject(ScrollDispatcher);

    constructor() {
        this.scrollDispatcher
            .scrolled()
            .pipe(takeUntilDestroyed())
            .subscribe(() => {
                this.quickActionPopover()?.hide();
            });
    }

    /**
     * Computed signal for toolbar buttons, reacting to row selection changes.
     *
     * @returns {TableToolbarButton[]} List of configured toolbar buttons.
     */
    public toolbarButtons = computed<TableToolbarButton[]>(() => {
        const selectionCount = this.selectedRows().length;
        return [
            {
                buttonName: 'Bulk Delete',
                icon: 'delete',
                variant: selectionCount > 0 ? 'danger' : 'secondary',
            },
            {
                buttonName: 'Export',
                icon: 'export',
                variant: 'accent',
            },
        ];
    });

    /**
     * Handles generic action click events from the license table.
     *
     * @param {Object} event - The action event data.
     * @param {string} event.action - The name of the action performed.
     * @param {UILicense} event.row - The row data associated with the action.
     * @returns {void}
     */
    public onActionClick(event: { action: string; row: UILicense }): void {
        console.log('Action clicked:', event.action, 'Row data:', event.row);
    }

    /**
     * Handles cell click events for redirection-enabled columns.
     *
     * @param {Object} event - The cell click event data.
     * @param {string} event.field - The field/column key clicked.
     * @param {string | number | undefined} event.value - The value of the cell.
     * @param {UILicense} event.row - The full row object.
     * @returns {void}
     */
    public onCellClick(event: {
        field: string;
        value: string | number | undefined;
        row: UILicense;
    }): void {
        if (event.row.id) {
            const url = `/licenses/${event.row.id}`;
            window.open(url, '_blank');
        }
    }

    /**
     * Handles row selection changes in the table.
     *
     * @param {UILicense[]} selectedRows - The array of currently selected rows.
     * @returns {void}
     */
    public onSelectionChange(selectedRows: UILicense[]): void {
        this.selectedRows.set(selectedRows);
    }

    /**
     * Handles clicks on global toolbar actions (e.g., Bulk Delete, Export).
     *
     * @param {string} action - The unique identifier of the action clicked.
     * @returns {void}
     */
    public onToolbarAction(action: string): void {
        if (action === 'Bulk Delete') {
            const selectedIds = this.selectedRows()
                .map((row) => row.id)
                .filter(Boolean);
            if (selectedIds.length > 0) {
                this.showDeleteModal.set(true);
            }
        } else if (action === 'Export') {
            const dataToExport =
                this.selectedRows().length > 0 ? this.selectedRows() : this.licenseData();

            this.exportService.exportToExcel(dataToExport, 'Licenses', this.licenseColumns);
        }
    }

    /**
     * Triggers a manual refresh of the license data by updating the refresh signal.
     *
     * @returns {void}
     */
    public refreshLicenses(): void {
        this.refreshTrigger.update((v) => v + 1);
    }

    /**
     * Handles search input updates.
     *
     * @param {string} term - The debounced search string from the table panel.
     * @returns {void}
     */
    public onSearch(term: string): void {
        this.searchTerm.set(term);
    }

    /**
     * Closes the bulk delete confirmation modal.
     *
     * @returns {void}
     */
    public closeDeleteModal(): void {
        this.showDeleteModal.set(false);
    }

    /**
     * Toggles the selection of a license in the assign modal.
     * @param id - The ID of the license to toggle
     */
    public toggleLicenseSelection(id: string): void {
        this.selectedAssignLicenseIds.update((ids) => {
            const newIds = new Set(ids);
            if (newIds.has(id)) {
                newIds.delete(id);
            } else {
                newIds.add(id);
            }
            return newIds;
        });
    }

    /**
     * Handles the "Assign Licenses" button click from the modal.
     */
    public onAssignLicenses(): void {
        const selectedIdsArray = Array.from(this.selectedAssignLicenseIds());
        console.log('Assigning licenses:', selectedIdsArray);

        // Perform mock assignment logic
        // In a real app, this would call a service method
        this.isAssignModalVisible.set(false);
        this.selectedAssignLicenseIds.set(new Set());
    }
    /**
     * Executes the bulk deletion of selected licenses after user confirmation.
     *
     * @returns {void}
     */
    public onConfirmBulkDelete(): void {
        const selectedIds = this.selectedRows()
            .map((row) => row.id)
            .filter(Boolean);
        if (selectedIds.length > 0) {
            this.licensesService
                .bulkDelete(selectedIds)
                .pipe(withLoading(this.isLoading), takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: () => {
                        this.showDeleteModal.set(false);
                        this.showSuccessModal.set(true);
                        this.selectedRows.set([]);
                        this.refreshLicenses();
                    },
                    error: (err: unknown) => {
                        Logger.error('SingleHostComponent', 'Error bulk deleting licenses:', err);
                    },
                });
        }
    }
}
